import { useState } from "react";
import type { ReactNode } from "react";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
} from "@elaniintc/goes-design-system/ui";
import { cn } from "@/utils/theme";
import { Link } from "react-router";
import type { Notification, NotificationLink } from "./types";
import { DetailLog } from "./detail-log";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

interface NotificationCardProps {
  notification: Notification;
  onToggleRead: (id: string) => void;
  onToggleUnread: (id: string) => void;
  customDescription?: ReactNode;
}

/**
 * Clickable link component for notification descriptions.
 * Redirects to the origin of the action when clicked.
 */
const ActionLink = ({ link }: { link: NotificationLink }) => (
  <Link
    to={link.url}
    className="font-bold text-action hover:underline cursor-pointer"
  >
    {link.label}
  </Link>
);

/**
 * Renders a description string with the first matching link label replaced
 * by a clickable ActionLink, preserving surrounding text unchanged.
 */
const renderDescriptionWithLinks = (
  description: string,
  requestLink?: NotificationLink,
  formLink?: NotificationLink,
): React.ReactNode => {
  const link = requestLink ?? formLink;
  if (!link) return description;

  const index = description.indexOf(link.label);
  if (index === -1) return description;

  return (
    <>
      {description.slice(0, index)}
      <ActionLink link={link} />
      {description.slice(index + link.label.length)}
    </>
  );
};

/**
 * Expandable long content component with "Ver más" button.
 */
const ExpandableContent = ({ content }: { content: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="w-full">
      <p
        className={cn(
          "text-size-body-md text-body font-normal leading-7 w-full whitespace-pre-wrap",
          !isExpanded && "line-clamp-3",
        )}
      >
        {content}
      </p>
      <Button
        variant="ghost"
        size="xs"
        className="text-action hover:text-action-loud p-0 h-auto mt-1"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? "Ver menos" : "Ver más"}
      </Button>
    </div>
  );
};

export const NotificationCard = ({
  notification,
  onToggleRead,
  customDescription,
  onToggleUnread,
}: NotificationCardProps) => {
  const hasLongContent = !!notification.longContent;
  const { hasPermission } = useAuthorization();
  const canUpdateNotifications = hasPermission(
    Permissions.notifications.update,
  );

  return (
    <div
      className={cn(
        "flex gap-0 items-start p-2 rounded-lg w-full",
        "bg-neutral-object",
      )}
    >
      <div className="flex flex-col flex-1 gap-2 items-start justify-center min-w-0">
        {/* Header with avatar, name, date and read button */}
        <div className="flex gap-2 items-start w-full">
          <div className="flex flex-1 gap-2 items-center min-w-0">
            {/* Avatar */}
            <Avatar
              size="md"
              shape="circle"
              className="shrink-0 size-9 border-action-quiet/50"
            >
              <AvatarFallback color="action" shape="circle" type="ghost">
                {notification.initial}
              </AvatarFallback>
            </Avatar>

            {/* Username and Date */}
            <div className="flex flex-col items-start shrink-0">
              <span className="text-action font-semibold text-size-body-md leading-7">
                {notification.username}
              </span>
              <div className="flex items-center gap-1 text-body-quiet text-size-body-sm -mb-1">
                <Icon
                  type="far"
                  name="calendar-clock"
                  className="text-current"
                />
                <span className="font-normal">{notification.date}</span>
              </div>
            </div>
          </div>

          {/* Read/Unread toggle button */}
          <Button
            variant="ghost"
            size="xs"
            disabled={!canUpdateNotifications}
            className={cn(
              "shrink-0 min-w-8",
              notification.isRead ? "text-body-quiet" : "text-action",
            )}
            onClick={() => {
              if (canUpdateNotifications && notification.isRead) {
                onToggleUnread(notification.id);
              } else if (canUpdateNotifications && !notification.isRead) {
                onToggleRead(notification.id);
              }
            }}
            aria-label={
              notification.isRead ? "Marcar como no leído" : "Marcar como leído"
            }
          >
            <Icon
              type="far"
              name={notification.isRead ? "envelope-open" : "envelope"}
              className="text-sm"
            />
          </Button>
        </div>

        {/* Long content with expandable "Ver más" */}
        {hasLongContent && (
          <>
            {customDescription ? (
              <div className="text-body text-size-body-md font-normal leading-7 w-full whitespace-pre-wrap">
                {customDescription}
              </div>
            ) : (
              <ExpandableContent content={notification.longContent!} />
            )}
          </>
        )}

        {/* Description with clickable links inlined within the message text */}
        {!hasLongContent && (
          <p className="text-body text-size-body-md font-normal leading-7 w-full">
            {customDescription ||
              renderDescriptionWithLinks(
                notification.description,
                notification.requestLink,
                notification.formLink,
              )}
          </p>
        )}

        {/* Detail Log - Visual representation of the change */}
        {notification.detail && <DetailLog detail={notification.detail} />}
      </div>
    </div>
  );
};
